import { useIntl } from 'react-intl';

import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { useExperiment } from '@xing-com/hub';
import {
  IconCompanies,
  IconCompaniesFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconJobsFilled,
  IconSearch,
  IconSearchFilled,
} from '@xing-com/icons';
import { ProductsMenuWithItemsLoggedOut } from '@xing-com/platform-layout-products-menu';

import { ActivityCenterLoginButton } from '../activity-center-login-button';
import { CreateJobButton } from '../create-job-button';
import {
  ActionButtonSpace,
  ButtonWithMarkerContainer,
  StyledOnlyOnBigScreenContainer,
} from '../header/header.styled';
import { NavItemHeader } from '../nav-item-header';

type HeaderNavItemsLoggedOutProps = {
  loginLink?: string;
};

type NavigationVariant = 'A' | 'B' | 'C' | 'D';

const getNavigationVariant = (variant: string): NavigationVariant => {
  return variant === 'B' || variant === 'C' || variant === 'D' ? variant : 'A';
};

export const HeaderNavItemsLoggedOut: React.FC<
  HeaderNavItemsLoggedOutProps
> = ({ loginLink }) => {
  const { formatMessage } = useIntl();
  const variant = getNavigationVariant(useExperiment('ABACUS-568'));
  const isReducedNavigation = variant !== 'A';
  const isRegistrationFocus = variant === 'D';
  const showCreateJobMarker = variant === 'C';
  const CreateJobButtonContainer = showCreateJobMarker
    ? ButtonWithMarkerContainer
    : ActionButtonSpace;

  return (
    <>
      <StyledOnlyOnBigScreenContainer>
        {isReducedNavigation ? (
          <NavItemHeader
            activeItemValues={['jobs', 'job-search']}
            icon={IconSearch}
            activeIcon={IconSearchFilled}
            horizontal
            title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
            href="https://www.xing.com/"
            tracking={{
              nwt_nav: 'find_jobs',
            }}
          />
        ) : (
          <>
            <NavItemHeader
              activeItemValues={['jobs']}
              icon={IconJobs}
              activeIcon={IconJobsFilled}
              title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
              href="https://www.xing.com/"
              tracking={{
                nwt_nav: 'find_jobs',
              }}
            />
            <NavItemHeader
              activeItemValues={['companies']}
              icon={IconCompanies}
              activeIcon={IconCompaniesFilled}
              title={formatMessage({ id: 'MAINNAV_COMPANIES' })}
              href="https://www.xing.com/companies"
              tracking={{
                nwt_nav: 'companies',
              }}
            />
            <NavItemHeader
              icon={IconContacts}
              activeIcon={IconContactsFilled}
              title={formatMessage({ id: 'MAINNAV_NETWORK' })}
              href="https://www.xing.com/network"
              activeItemValues={['members']}
              tracking={{
                nwt_nav: 'network',
              }}
            />
            <NavItemHeader
              icon={IconSearch}
              activeIcon={IconSearchFilled}
              title={formatMessage({ id: 'FRAME_HEADER_SEARCH' })}
              href={`https://www.xing.com${XING_JOBS_SEARCH_KI_PATH}`}
              activeItemValues={['job-search']}
              tracking={{
                nwt_nav: 'job_search',
              }}
            />
          </>
        )}
      </StyledOnlyOnBigScreenContainer>
      {isReducedNavigation ? (
        <>
          <CreateJobButtonContainer>
            <CreateJobButton
              showMarker={showCreateJobMarker}
              variant={isRegistrationFocus ? 'secondary' : 'primary'}
            />
          </CreateJobButtonContainer>
          <ActionButtonSpace>
            <ActivityCenterLoginButton
              variant={isRegistrationFocus ? 'primary' : 'secondary'}
              title={formatMessage({ id: 'MAINNAV_LOGIN' })}
              href="https://www.xing.com/activity"
              loginLink={loginLink}
              nwtNav="boneyard_activitycenter"
              openSource="reduced_navigation"
            />
          </ActionButtonSpace>
        </>
      ) : (
        <>
          <ActivityCenterLoginButton
            title={formatMessage({ id: 'MAINNAV_LOGIN' })}
            href="https://www.xing.com/activity"
            loginLink={loginLink}
          />
          <ActionButtonSpace>
            <CreateJobButton />
          </ActionButtonSpace>
          <StyledOnlyOnBigScreenContainer>
            <ProductsMenuWithItemsLoggedOut />
          </StyledOnlyOnBigScreenContainer>
        </>
      )}
    </>
  );
};
